"use client";

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useRouter } from "next/navigation";
import {
  listMyMemberships,
  createOrganization,
  leaveOrganization,
  getListMyMembershipsQueryKey,
  getListProjectsQueryKey,
  getListNotificationsQueryKey,
  getGetUnreadCountQueryKey,
} from "@/lib/api/generated";
import type { CreateOrgRequest } from "@/lib/api/model";
import { showToast } from "@/components/ui/sonner";
import { resetOnboardingCheck } from "@/components/layout/onboarding-guard";
import { invalidateCompliancePresentationQueries } from "@/features/compliance/lib/invalidate-presentation-cache";
import {
  ORGANIZATION_CONTEXT_SETUP_ERROR_MESSAGE,
  refreshOrganizationContext,
} from "@/lib/api/org-context";

// Org switch refreshes the org-scoped data roots. Refresh the auth/org
// context first via `refreshOrganizationContext`, then call this to mark
// the org-scoped queries stale so any mounted view re-fetches under the
// new org.
export async function resetOrgCache(
  qc: ReturnType<typeof useQueryClient>,
  _router: ReturnType<typeof useRouter>,
) {
  await Promise.all([
    qc.invalidateQueries({ queryKey: ["auth"] }),
    qc.invalidateQueries({ queryKey: getListMyMembershipsQueryKey() }),
    qc.invalidateQueries({ queryKey: getListProjectsQueryKey() }),
    qc.invalidateQueries({ queryKey: ["projects", "navbar"] }),
    qc.invalidateQueries({ queryKey: getListNotificationsQueryKey() }),
    qc.invalidateQueries({ queryKey: getGetUnreadCountQueryKey() }),
    invalidateCompliancePresentationQueries(qc),
  ]);
}

export function useOrgMemberships(options?: {
  enabled?: boolean;
  search?: string;
}) {
  return useQuery({
    queryKey: ["org", "memberships", { search: options?.search }],
    queryFn: () => listMyMemberships(),
    placeholderData: (prev) => prev,
    enabled: options?.enabled ?? true,
    select: (data) => {
      if (!options?.search) return data;
      const q = options.search.toLowerCase();
      return {
        ...data,
        memberships: data.memberships.filter((m) =>
          m.org_name?.toLowerCase().includes(q),
        ),
      };
    },
  });
}

export function useCreateOrg() {
  const qc = useQueryClient();
  const router = useRouter();

  return useMutation({
    mutationFn: (data: CreateOrgRequest) => createOrganization(data),
    onSuccess: async (response) => {
      try {
        if (!response.workos_org_id) {
          throw new Error(ORGANIZATION_CONTEXT_SETUP_ERROR_MESSAGE);
        }
        await refreshOrganizationContext(response.workos_org_id);
      } catch (err) {
        const description =
          err instanceof Error
            ? err.message
            : "Please try again before continuing.";

        showToast.error("Failed to refresh organization context", {
          description,
        });
        return;
      }

      resetOnboardingCheck();
      router.push("/projects");
      await resetOrgCache(qc, router);
    },
    onError: (err: Error) => {
      showToast.error("Failed to create organization", {
        description: err.message,
      });
    },
  });
}

export function useLeaveOrg() {
  const qc = useQueryClient();
  const router = useRouter();

  return useMutation({
    mutationFn: (data?: { transfer_to_user_id?: string }) =>
      leaveOrganization(data ?? {}),
    onSuccess: async (res) => {
      resetOnboardingCheck();
      if (res.active_org) {
        router.push("/projects");
      }
      await resetOrgCache(qc, router);
    },
    onError: (err: Error) => {
      showToast.error("Organization error", { description: err.message });
    },
  });
}
